import { useEffect, useState } from "react";
import { Box } from "../../../shared/components/Box";
import { Container } from "../../../shared/components/Container";
import { Grid } from "../../../shared/components/Grid";
import { Panel } from "../../../shared/components/Panel";
import { Layout } from "../../../layouts/Layout";
import { loadDashboardStatus, type DashboardCamera, type DashboardRuntime } from "../api";
import { Gauge } from "../components/Gauge";
import "../pages/DashboardPage.scss";

type DashboardData = { cameras: DashboardCamera[]; runtime: DashboardRuntime };

function DashboardLink({ href, children }: { href: string; children: string }) {
  return <a className="dashboard-link" href={href}>{children} →</a>;
}

export function DashboardDesktopView() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    loadDashboardStatus()
      .then((next) => {
        if (active) setData(next);
      })
      .catch((error) => {
        if (active) setLoadError(error instanceof Error ? error.message : "Dashboard data unavailable");
      });
    return () => { active = false; };
  }, []);

  const cameras = data?.cameras ?? [];
  const online = cameras.filter((camera) => camera.status === "Live").length;
  const degraded = cameras.filter((camera) => camera.status !== "Live");
  const runtime = data?.runtime;
  const runtimeIssue = runtime?.state === "error";
  const attentionCount = degraded.length + Number(runtimeIssue);
  const readiness = cameras.length ? Math.round((online / cameras.length) * 100) : 0;

  return (
    <Layout activePath="/" className="dashboard-page">
      <Box as="main" className="main">
        <header className="header">
          <div>
            <p className="eyebrow">Operations overview</p>
            <h1 className="title">Dashboard</h1>
          </div>
          <DashboardLink href="/cameras">Open cameras</DashboardLink>
        </header>
        <Container className="content" fluid>
          {loadError ? <p className="dashboard-error">{loadError}</p> : null}
          <Grid as="section" className="summary">
            <article><p>Cameras online</p><strong>{online} <small>/ {cameras.length}</small></strong></article>
            <article><p>Needs attention</p><strong className={attentionCount ? "warning" : "healthy-text"}>{attentionCount}</strong></article>
            <article><p>Workflow runtime</p><strong className={`runtime-state runtime-state--${runtime?.state ?? "stopped"}`}>{runtime?.state ?? "Loading"}</strong></article>
          </Grid>
          <Grid as="section" className="dashboard-grid">
            <Panel as="article" className="health">
              <div className="panel-heading"><div><h2>System health</h2><p>Camera availability and workflow state</p></div><span className={readiness === 100 && !runtimeIssue ? "healthy" : "degraded"}>{readiness === 100 && !runtimeIssue ? "Healthy" : "Check system"}</span></div>
              <Gauge label="cameras online" value={readiness} />
              <div className="health-stats"><div><b>{online}/{cameras.length}</b><span>Online</span></div><div><b>{degraded.length}</b><span>Degraded</span></div><div><b>{runtime?.state ?? "—"}</b><span>Workflow</span></div></div>
            </Panel>
            <Panel as="section" className="attention">
              <div className="panel-heading"><div><h2>Needs attention</h2><p>Current operational exceptions</p></div><DashboardLink href="/alerts">Alerts</DashboardLink></div>
              <div className="attention-list">
                {runtimeIssue ? <article><b>Workflow runtime error</b><span>{runtime?.error ?? "Inspect workflow configuration and runtime logs."}</span><DashboardLink href="/workflow">Open workflow</DashboardLink></article> : null}
                {degraded.map((camera) => <article key={camera.id}><b>{camera.id} is {camera.status.toLowerCase()}</b><span>Inspect camera stream and configuration.</span><DashboardLink href="/cameras">Open cameras</DashboardLink></article>)}
                {!runtimeIssue && degraded.length === 0 ? <p className="empty">No operational exceptions.</p> : null}
              </div>
            </Panel>
          </Grid>
          <Grid as="section" className="quick-links">
            <article><h2>Camera monitoring</h2><p>View live feeds, stream state, and camera configuration.</p><DashboardLink href="/cameras">Open cameras</DashboardLink></article>
            <article><h2>Alerts</h2><p>Review and acknowledge operator-attention items.</p><DashboardLink href="/alerts">Open alerts</DashboardLink></article>
            <article><h2>Events</h2><p>Review detected activity and system history.</p><DashboardLink href="/events">Open events</DashboardLink></article>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
}
